import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ReportMetricCard from "../shared/ReportMetricCard";

/**
 * DashboardReport - Template for dashboard-style reports with metrics and charts
 *
 * @param {string} title - Report title
 * @param {string} description - Report description
 * @param {Function} dataFetchFn - Function to fetch data, receives filters as param
 * @param {Object} filters - Current filter values
 * @param {Function} metricsConfig - Function that receives data and returns metrics array
 * @param {Function} chartsConfig - Function that receives data and returns chart components
 */
const DashboardReport = ({
  title,
  description,
  dataFetchFn,
  filters = {},
  metricsConfig,
  chartsConfig,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!dataFetchFn) {
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await dataFetchFn(filters);
      setData(response.data || {});
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load data");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dataFetchFn, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Memoize metrics and charts to prevent infinite re-renders
  const metrics = useMemo(() => {
    return data && metricsConfig ? metricsConfig(data) : [];
  }, [data, metricsConfig]);

  const charts = useMemo(() => {
    return data && chartsConfig ? chartsConfig(data) : null;
  }, [data, chartsConfig]);

  // Check if metrics are grouped (array of groups with groupTitle and metrics)
  const isGrouped = metrics.length > 0 && metrics[0]?.groupTitle !== undefined;

  const getGridSize = useCallback((numMetrics) => {
    if (numMetrics === 1) {
      return { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 };
    }

    if (numMetrics === 2) {
      return { xs: 12, sm: 6, md: 6, lg: 6, xl: 6 };
    }

    if (numMetrics === 3) {
      return { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };
    }

    if (numMetrics === 4) {
      return { xs: 12, sm: 6, md: 6, lg: 3, xl: 3 };
    }

    if (numMetrics === 5) {
      return { xs: 12, sm: 6, md: 4, lg: 2.4, xl: 2.4 };
    }

    return { xs: 12, sm: 6, md: 4, lg: 4, xl: 2 };
  }, []);

  return (
    <Box>
      {/* Header */}
      {(title || description) && (
        <Box sx={{ mb: 3 }}>
          {title && (
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Dashboard Content */}
      {!loading && !error && data && (
        <Box>
          {/* Metrics - Grouped or Flat */}
          {metrics.length > 0 && (
            <Box sx={{ mb: 3, p: 0.5 }}>
              {isGrouped ? (
                // Grouped metrics with section headers
                metrics.map((group, groupIndex) => (
                  <Box key={groupIndex} sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      sx={{
                        mb: 2,
                        color: theme.palette.text.primary,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      {group.groupTitle}
                    </Typography>
                    <Grid container spacing={2}>
                      {group.metrics.map((metric, metricIndex) => {
                        const gridSize = getGridSize(group.metrics.length);

                        return (
                          <Grid key={metricIndex} size={gridSize}>
                            <ReportMetricCard {...metric} />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))
              ) : (
                // Flat metrics (backward compatible)
                <Grid container spacing={2}>
                  {(() => {
                    const gridSize = getGridSize(metrics.length);
                    return metrics.map((metric, index) => (
                      <Grid key={index} size={gridSize}>
                        <ReportMetricCard {...metric} />
                      </Grid>
                    ));
                  })()}
                </Grid>
              )}
            </Box>
          )}

          {/* Charts Section */}
          {charts && <Box sx={{ mt: 3, p: 0.5 }}>{charts}</Box>}

          {/* No Data State */}
          {metrics.length === 0 && !charts && (
            <Box
              sx={{
                py: 8,
                textAlign: "center",
                bgcolor: theme.palette.background.alt,
                borderRadius: "12px",
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No data available for the selected filters
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DashboardReport;
